'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { QueryClient, QueryClientProvider, isServer } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { IsNavigationCondensedManager } from '~/system/providers/IsNavigationCondensed/IsNavigationCondensed.manager';
import { recomputeViewport } from '~/system/recomputeViewport';
import { AnalyticsProvider } from '~/ui/components/analytics/context';
import { Route } from '~/ui/systems/internalLink/route';

export type BodyProps = React.ComponentProps<'body'>;

// The below code comes from the react-query docs
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }
    return browserQueryClient;
  }
}

// TODO: When v2 is complete, <BreadcrumbProvider /> should be rendered here.
export function Body({ children, style, ...props }: BodyProps) {
  const pathname = usePathname();

  useEffect(() => {
    recomputeViewport();
    window.addEventListener('resize', recomputeViewport);
    return () => window.removeEventListener('resize', recomputeViewport);
  }, []);

  return (
    <body
      {...props}
      suppressHydrationWarning={true}
      style={{
        ...style,
        ['--scroll-progress' as never]: pathname === Route.HOME ? '0' : '1',
      }}
    >
      <IsNavigationCondensedManager>
        <QueryClientProvider client={getQueryClient()}>
          <ReactQueryDevtools initialIsOpen={false} />
          <AnalyticsProvider>{children}</AnalyticsProvider>
        </QueryClientProvider>
      </IsNavigationCondensedManager>
    </body>
  );
}
